import React, { useEffect, useState } from 'react';
import AdminTable from 'common/AdminTable';
import { toast } from 'react-toastify';
import axiosInstance from 'apis/axiosConfig';

const ManageLoansList = () => {
    const [data, setData] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [currentLoan, setCurrentLoan] = useState(null);

    const fetchData = async () => {
        try {
            const res = await axiosInstance.get('loans-data');
            setData(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const calculateLoanDetails = (principal, tenureMonths, annualInterestRate) => {
        const P = parseFloat(principal) || 0;
        const N = parseInt(tenureMonths) || 0;
        const R = parseFloat(annualInterestRate) || 0;
        
        if (P === 0 || N === 0) return { emi: 0, totalPay: 0 };
        
        if (R === 0) {
            return { emi: (P / N).toFixed(2), totalPay: P.toFixed(2) };
        }

        const r = R / 12 / 100;
        const emi = (P * r * Math.pow(1 + r, N)) / (Math.pow(1 + r, N) - 1);
        const totalPay = emi * N;
        
        return { emi: emi.toFixed(2), totalPay: totalPay.toFixed(2) };
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleActionClick = (row) => {
        setCurrentLoan(row);
        setShowModal(true);
    };

    const handleSave = async () => {
        try {
            const details = calculateLoanDetails(currentLoan.loan_amount, currentLoan.loan_tenure_months, currentLoan.interest_rate);
            
            await axiosInstance.put(`loans-data/${currentLoan.id}`, {
                loan_status: currentLoan.loan_status,
                loan_start_date: currentLoan.loan_start_date,
                interest_rate: currentLoan.interest_rate,
                monthly_emi: details.emi,
                total_loan_pay: details.totalPay,
                remaining_amount: details.totalPay,
                remaining_emi_count: currentLoan.loan_tenure_months
            });
            toast.success("Loan Updated & Calculated Successfully!");
            setShowModal(false);
            fetchData();
        } catch (err) {
            toast.error("Failed to update status");
        }
    };

    const getStatusBadge = (status) => {
        if(status === 'active') return 'badge-success';
        if(status === 'pending') return 'badge-warning';
        if(status === 'rejected') return 'badge-danger';
        return 'badge-info';
    }

    const getLoanFrequencyData = (row) => {
        const paidCount = parseInt(row.emi_paid_count || 0);
        const remCount = parseInt(row.remaining_emi_count || 0);
        const rawTenure = parseInt(row.loan_tenure_months || 12);

        let totalTenure = row.total_tenure_count || (paidCount + remCount) || rawTenure;
        let emiFreq = row.emi_frequency || 'Monthly';
        let tenureUnit = emiFreq === 'Daily' ? 'Days' : emiFreq === 'Yearly' ? 'Years' : 'Mo';

        return { totalTenure, tenureUnit };
    };

    const tableHeaders = [
        { Header: 'Loan ID', accessor: 'id' },
        { Header: 'User ID', accessor: 'loan_user_id' },
        { Header: 'Amount', accessor: 'loan_amount', Cell: ({ row }) => `₹${row.original.loan_amount}` },
        { 
            Header: 'Tenure', 
            accessor: 'loan_tenure_months', 
            Cell: ({ row }) => {
                const { totalTenure, tenureUnit } = getLoanFrequencyData(row.original);
                return `${totalTenure} ${tenureUnit}`;
            }
        },
        { Header: 'Int. Rate', accessor: 'interest_rate', Cell: ({ row }) => `${row.original.interest_rate || 0}%` },
        { Header: 'Total Pay', accessor: 'total_loan_pay', Cell: ({ row }) => {
            const details = calculateLoanDetails(row.original.loan_amount, row.original.loan_tenure_months, row.original.interest_rate);
            return <strong className="text-success">₹{details.totalPay}</strong>;
        }},
        { Header: 'Status', accessor: 'loan_status', Cell: ({ row }) => (<span className={`badge ${getStatusBadge(row.original.loan_status)}`}>{row.original.loan_status.toUpperCase()}</span>) },
        { Header: 'Action', accessor: 'action', Cell: ({ row }) => (<button className="btn btn-warning btn-sm text-dark font-weight-bold" onClick={() => handleActionClick(row.original)}><i className="fa fa-edit"></i> Edit Status</button>) }
    ];

    return (
        <div className='container-fluid mt-5'>
            <div className='mainheadig mx-4 d-flex'>
                <h4 className='text-white font-weight-bold'>Manage Loans</h4>
            </div>
            {data?.length > 0 ? (
                <AdminTable tableHeaders={tableHeaders} tableData={data} />
            ) : <p className='container-fluid bg-danger w-100 p-2 mx-4 text-center text-white'>No data found</p>}

            {/* Edit Loan Modal */}
            {showModal && currentLoan && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-warning">
                                <h5 className="modal-title text-dark font-weight-bold">Update Loan Request</h5>
                                <button type="button" className="close" onClick={() => setShowModal(false)}>&times;</button>
                            </div>
                            <div className="modal-body row">
                                <div className="col-12 mb-2"><strong>Loan Amount requested:</strong> <span className="text-primary">₹{currentLoan.loan_amount}</span> for {currentLoan.loan_tenure_months} Months</div>
                                <div className="col-12 mb-3">
                                    <label htmlFor="interestRate"><strong>Interest Rate (Annual %):</strong></label>
                                    <input id="interestRate" type="number" className="form-control w-50" value={currentLoan.interest_rate || 0} onChange={(e) => setCurrentLoan({...currentLoan, interest_rate: parseFloat(e.target.value) || 0})} />
                                </div>
                                <div className="col-12 mb-2">
                                    <strong>Calculated Monthly EMI:</strong> <span className="text-danger font-weight-bold">₹{calculateLoanDetails(currentLoan.loan_amount, currentLoan.loan_tenure_months, currentLoan.interest_rate).emi}</span>
                                </div>
                                <div className="col-12 mb-4">
                                    <strong>Total Payable Amount:</strong> <span className="text-success font-weight-bold">₹{calculateLoanDetails(currentLoan.loan_amount, currentLoan.loan_tenure_months, currentLoan.interest_rate).totalPay}</span>
                                </div>
                                
                                <div className="col-12 mb-3">
                                    <label htmlFor="loanStatus"><strong>Change Loan Status:</strong></label>
                                    <select id="loanStatus" className="form-control" value={currentLoan.loan_status} onChange={(e) => setCurrentLoan({...currentLoan, loan_status: e.target.value})}>
                                        <option value="pending">Pending</option>
                                        <option value="active">Active (Approve)</option>
                                        <option value="completed">Completed</option>
                                        <option value="rejected">Rejected</option>
                                    </select>
                                </div>
                                <div className="col-12 mb-3">
                                    <label htmlFor="startDate"><strong>Start Date (if approving):</strong></label>
                                    <input id="startDate" type="date" className="form-control" value={currentLoan.loan_start_date || ''} onChange={(e) => setCurrentLoan({...currentLoan, loan_start_date: e.target.value})} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                                <button type="button" className="btn btn-primary" onClick={handleSave}>Save Changes</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
export default ManageLoansList;
